import React from 'react';
import {
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesPageHeader,
} from '@agnes/ui';
import { Download, SlidersHorizontal } from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import { SearchInput } from '@/components/SearchInput';
import {
  type LinhaClassificacaoAnalise,
  useCboClassificacaoAnaliseStore,
  useClassificacaoAnaliseStore,
  useHorariosClassificacaoAnaliseStore,
} from '@/stores/useClassificacaoAnaliseStore';
import ClassificacaoAnaliseFilter from './ClassificacaoAnaliseFilter';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';
import { BadgeGrauExposicaoRisco } from '@/components/analise/BadgeGrauExposicaoRisco';
import {
  AnaliseGrauExposicaoRiscoNome,
  GRAU_EXPOSICAO_RISCO_CHAVES,
} from '@/constants/grau-exposicao-risco/GrauExposicaoRisco';

// os 3 domínios usam a MESMA tela; só mudam o store, os textos e o prefixo de data-qa.
type Store = typeof useClassificacaoAnaliseStore;
export interface ClassificacaoAnaliseConfig {
  store: Store;
  titulo: string;
  subtitulo: string;
  qa: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export const CONFIG_RUBRICAS: ClassificacaoAnaliseConfig = {
  store: useClassificacaoAnaliseStore,
  titulo: 'Rubricas - Classificação de Análise',
  subtitulo:
    'Gerenciamento de padrões de observações adotadas pela equipe técnica referente análise de Rubricas',
  qa: 'classificacao-analise',
};
// eslint-disable-next-line react-refresh/only-export-components
export const CONFIG_CBO: ClassificacaoAnaliseConfig = {
  store: useCboClassificacaoAnaliseStore,
  titulo: 'CBO - Classificação de Análise',
  subtitulo:
    'Gerenciamento de padrões de observações adotadas pela equipe técnica referente análise de CBO',
  qa: 'classificacao-analise-cbo',
};
// eslint-disable-next-line react-refresh/only-export-components
export const CONFIG_HORARIOS: ClassificacaoAnaliseConfig = {
  store: useHorariosClassificacaoAnaliseStore,
  titulo: 'Horários e Jornada - Classificação de Análise',
  subtitulo:
    'Gerenciamento de padrões de observações adotadas pela equipe técnica referente análise de Horários e Jornadas',
  qa: 'classificacao-analise-horarios',
};

export default function PageClassificacaoAnalise({
  config = CONFIG_RUBRICAS,
}: {
  config?: ClassificacaoAnaliseConfig;
} = {}) {
  const { store, titulo, subtitulo, qa } = config;
  const lista = store((s) => s.lista);
  const total = store((s) => s.total);
  const page = store((s) => s.page);
  const size = store((s) => s.size);
  const carregando = store((s) => s.carregando);
  const exportando = store((s) => s.exportando);
  const busca = store((s) => s.busca);
  const sortBy = store((s) => s.sortBy);
  const sortOrder = store((s) => s.sortOrder);
  const filtros = store((s) => s.filtros);
  const opcoes = store((s) => s.opcoes);

  React.useEffect(() => {
    store.getState().carregarOpcoes();
    store.getState().buscar();
  }, [store]);

  const filtrosAtivos = Object.values(filtros).filter(Boolean).length;

  const columns: AgnesDataTableColumn<LinhaClassificacaoAnalise>[] = [
    { key: 'codigo', header: '#', width: 80, sortable: true, render: (r) => r.codigo },
    {
      key: 'grauRisco',
      header: 'Grau de risco',
      width: 150,
      sortable: true,
      render: (r) => (
        <>
          <BadgeGrauExposicaoRisco
            grau={GRAU_EXPOSICAO_RISCO_CHAVES[r.grauRisco as AnaliseGrauExposicaoRiscoNome]}
            dataQa={`${qa}-risco-${r.codigo}`}
          />
        </>
      ),
    },
    {
      key: 'ocorrencia',
      header: 'Ocorrência',
      width: 280,
      sortable: true,
      render: (r) => r.ocorrencia,
    },
    {
      key: 'diagnostico',
      header: 'Diagnóstico',
      width: 380,
      sortable: true,
      render: (r) => (
        <span className="block max-w-[360px] truncate" title={r.diagnostico}>
          {r.diagnostico || '-'}
        </span>
      ),
    },
    { key: 'situacao', header: 'Situação', width: 120, sortable: true, render: (r) => r.situacao },
    {
      key: 'alteradoPor',
      header: 'Alterado por',
      width: 160,
      sortable: true,
      render: (r) => r.alteradoPor || '-',
    },
    {
      key: 'atualizacao',
      header: 'Atualização',
      width: 170,
      sortable: true,
      render: (r) => r.atualizacao || '-',
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<SlidersHorizontal />}
        title={titulo}
        subtitle={subtitulo}
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Parâmetros de análise', href: '/admin/parametros' },
          { label: titulo },
        ]}
      />

      <AgnesCard>
        <AgnesCardContent className="flex items-stretch">
          <div className="mb-3 flex items-center gap-2">
            <SearchInput
              value={busca}
              onValueChange={(v) => store.getState().setBusca(v)}
              onSearch={() => store.getState().buscar()}
              placeholder="Pesquisar por ocorrência ou diagnóstico"
            />
            <div className="ml-auto flex gap-2">
              <ClassificacaoAnaliseFilter
                opcoes={opcoes}
                count={filtrosAtivos}
                onApply={(f) => store.getState().aplicarFiltros(f)}
              />
              <ExportButton
                variant="outlined"
                startIcon={<Download className="h-4 w-4" />}
                disabled={exportando}
                onClick={() => store.getState().exportar()}
                data-qa={`${qa}-exportar`}
              >
                Exportar
              </ExportButton>
            </div>
          </div>

          <TableWrapper>
            <AgnesDataTable<LinhaClassificacaoAnalise>
              data-qa={`${qa}-tabela`}
              columns={columns}
              data={lista}
              rowKey={(r) => r.codigo}
              loading={carregando}
              sort={{
                sortBy,
                sortOrder,
                onSortChange: (by, order) => store.getState().setSort(by, order),
              }}
              pagination={{
                page,
                pageSize: size,
                total,
                serverSide: true,
                onPageChange: (p: number) => store.getState().setPage(p),
                onPageSizeChange: (s: number) => store.getState().setSize(s),
              }}
            />
          </TableWrapper>
        </AgnesCardContent>
      </AgnesCard>
    </>
  );
}
